import React, { ReactNode } from 'react';

import {
  BodyTypography,
  Button,
  ButtonIconPositionEnum,
  ButtonVariationsEnum,
  Caption,
  DateFormatEnum,
  FontSizeEnum,
  FontWeightEnum,
  formatDate,
  Link,
  LinkIconPositionsEnum,
  parseDateStringFromServer,
} from '@argenta/react-ui';
import { faChevronCircleRight } from '@fortawesome/pro-regular-svg-icons';
import { Trans } from '@lingui/macro';

import { SchedulesEnum } from '../../enums';
import { useI18nNavigate } from '../../hooks/navigation/useI18nNavigate';
import { VacanciesRoute } from '../../routers/AppRouter/routes';
import { VacancyBadges } from '../VacancyBadges';

import { VacancySummaryProps } from './VacancySummary.types';

import './VacancySummary.styles.scss';

export const VacancySummary: React.FunctionComponent<VacancySummaryProps> = ({
  id,
  link,
  dateAdded,
  title,
  description,
  location,
  schedule,
}) => {
  const navigate = useI18nNavigate();

  const renderVacancyDetailsLink = (): ReactNode => {
    if (link) {
      return (
        <Link
          iconPosition={LinkIconPositionsEnum.RIGHT}
          href={link}
          target="_top"
          rel="noopener noreferrer"
          icon={faChevronCircleRight}
        >
          <Trans id="vacancySummary.link.label" />
        </Link>
      );
    }

    return (
      <Button
        iconPosition={ButtonIconPositionEnum.RIGHT}
        icon={faChevronCircleRight}
        variation={ButtonVariationsEnum.LINK}
        onClick={() =>
          navigate({ pathname: VacanciesRoute.VACANCY_DETAILS, queryParams: { job: id } })
        }
      >
        <Trans id="vacancySummary.link.label" />
      </Button>
    );
  };

  const parsedDate = parseDateStringFromServer(dateAdded, DateFormatEnum.DATE_SERVER_ISO, true);

  return (
    <div className="argnt-vacancy-summary">
      <div className="argnt-vacancy-summary-row">
        <Caption fontSize={FontSizeEnum.BIG}>{title}</Caption>
        <span className="argnt-vacancy-summary-date">
          <BodyTypography fontSize={FontSizeEnum.BASE} fontWeight={FontWeightEnum.REGULAR}>
            {formatDate(parsedDate, DateFormatEnum.DATEPICKER_FORMAT)}
          </BodyTypography>
        </span>
      </div>

      {description && (
        <div className="argnt-vacancy-summary-row">
          <BodyTypography
            isParagraph
            fontSize={FontSizeEnum.BASE}
            fontWeight={FontWeightEnum.REGULAR}
          >
            {description}
          </BodyTypography>
        </div>
      )}

      <div className="argnt-vacancy-summary-row argnt-vacancy-summary-row--bottom">
        {renderVacancyDetailsLink()}
        <VacancyBadges schedule={schedule ?? SchedulesEnum.UNKNOWN} location={location} />
      </div>
    </div>
  );
};
